/**
 * Shared phone-number validation (admin-frontend UX tier).
 *
 * This is the frontend MIRROR of backend/src/shared/validatePhone.ts — the rule
 * must stay identical in both tiers. The frontend copy exists because the admin
 * app has no build-time import path into the backend package; if you change the
 * rule here, change it there too (and the backend jest tests guard the rule).
 *
 * Rules:
 *   - Allowed characters: digits, space, `-`, `.`, `(`, `)`, a single leading `+`,
 *     and an optional extension suffix (`x` / `ext` / `ext.` + digits).
 *   - After stripping formatting, the number portion must have 7–15 digits
 *     (E.164 max). Extension digits are NOT counted toward 7–15.
 *   - The value is NEVER rewritten/normalised — validate the raw string as typed.
 */

export interface PhoneValidationResult {
  ok: boolean
  error?: string
}

/** User-facing error, matching the ClientsView tone. */
export const PHONE_ERROR = 'Use digits, spaces, and + ( ) - . only (7–15 digits)'

const PHONE_SHAPE_RE = /^\+?[0-9 ().\-]+(?:\s*(?:x|ext|ext\.)\s*[0-9]+)?$/i
const EXTENSION_SPLIT_RE = /\s*(?:x|ext|ext\.)\s*/i

/** Count the significant phone digits (number portion only, excluding extension). */
export function countPhoneDigits(raw: string): number {
  const s = String(raw ?? '').trim()
  if (!s) return 0
  const [numberPart] = s.split(EXTENSION_SPLIT_RE)
  return (numberPart.match(/\d/g) || []).length
}

/**
 * Validate a phone string. Empty is INVALID here; callers treat phone as
 * optional by skipping validation when the field is blank.
 */
export function validatePhone(raw: string): PhoneValidationResult {
  const s = String(raw ?? '').trim()
  if (!s) return { ok: false, error: PHONE_ERROR }
  if (!PHONE_SHAPE_RE.test(s)) return { ok: false, error: PHONE_ERROR }
  const digits = countPhoneDigits(s)
  if (digits < 7 || digits > 15) return { ok: false, error: PHONE_ERROR }
  return { ok: true }
}

export default validatePhone
